import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingBag, Menu, X, Phone, Mail, ChevronDown, Truck } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { CONTACT_INFO, categories } from '../../data/mock';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showShop, setShowShop] = useState(false);
  const { cartCount, openCart } = useCart();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setShowShop(false);
  }, [location]);

  // Split navigation - left and right of centered logo
  const navLeft = [
    { name: 'Shop', path: '/shop', hasDropdown: true },
    { name: 'Why Us', path: '/why-choose-us' },
    { name: 'About', path: '/about' },
  ];
  const navRight = [
    { name: 'Materials', path: '/materials' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      {/* Top Announcement Bar - Scrolling marquee style */}
      <div className="bg-ink-900 text-cream-100 overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="flex items-center justify-between py-2.5 text-[11px] tracking-[0.15em] uppercase font-mono">
            <div className="hidden md:flex items-center gap-2 text-copper-300">
              <Truck className="w-3.5 h-3.5" />
              <span>Complimentary USA shipping on orders $75+</span>
            </div>
            <div className="flex-1 md:text-center text-center">
              <span className="text-cream-200">See better. Feel better.</span>
            </div>
            <div className="hidden md:flex items-center gap-4">
              <a href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`} className="flex items-center gap-1.5 hover:text-copper-400 transition-colors">
                <Phone className="w-3 h-3" />
                {CONTACT_INFO.phone}
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation - Centered logo with split nav */}
      <nav className={`transition-all duration-500 border-b ${
        isScrolled
          ? 'bg-cream-50/95 backdrop-blur-xl border-ink-100 shadow-soft'
          : 'bg-cream-50 border-transparent'
      }`}>
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="relative flex items-center justify-between h-20 lg:h-24">
            {/* Left Nav - Desktop */}
            <div className="hidden lg:flex items-center gap-10 flex-1">
              {navLeft.map((item) =>
                item.hasDropdown ? (
                  <div key={item.path} className="relative">
                    <button
                      onClick={() => setShowShop(!showShop)}
                      className={`flex items-center gap-1.5 text-[13px] tracking-[0.12em] uppercase font-medium transition-colors ${
                        location.pathname.includes('/shop')
                          ? 'text-ink-900'
                          : 'text-ink-600 hover:text-ink-900'
                      }`}
                    >
                      {item.name}
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showShop ? 'rotate-180' : ''}`} />
                    </button>
                    {showShop && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setShowShop(false)} />
                        <div className="absolute top-full left-0 mt-6 w-72 bg-cream-50 border border-ink-100 shadow-elevated py-3 z-20 animate-fade-up rounded-sm">
                          <Link to="/shop" className="block px-5 py-2.5 font-display text-ink-900 hover:bg-cream-100 text-base italic">
                            All Eyewear
                          </Link>
                          <div className="h-px bg-ink-100 mx-5 my-1" />
                          {categories.map((cat) => (
                            <Link
                              key={cat.id}
                              to={`/shop?category=${cat.slug}`}
                              className="block px-5 py-2 text-sm text-ink-600 hover:text-ink-900 hover:bg-cream-100 transition-colors"
                            >
                              {cat.name}
                            </Link>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`text-[13px] tracking-[0.12em] uppercase font-medium transition-colors ${
                      location.pathname === item.path
                        ? 'text-ink-900'
                        : 'text-ink-600 hover:text-ink-900'
                    }`}
                  >
                    {item.name}
                  </Link>
                )
              )}
            </div>

            {/* Centered Logo - Wordmark */}
            <Link to="/" className="flex flex-col items-center absolute left-1/2 -translate-x-1/2 group">
              <span className="font-display text-2xl lg:text-[28px] leading-none text-ink-900 tracking-tight group-hover:text-copper-600 transition-colors">
                Clarity <span className="italic font-light">Lens</span> Co
              </span>
              <span className="text-[9px] text-ink-400 tracking-[0.35em] uppercase mt-1 font-mono hidden sm:block">
                Eyewear &middot; Est. 2024
              </span>
            </Link>

            {/* Right Nav - Desktop */}
            <div className="hidden lg:flex items-center gap-10 flex-1 justify-end">
              {navRight.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`text-[13px] tracking-[0.12em] uppercase font-medium transition-colors ${
                    location.pathname === item.path
                      ? 'text-ink-900'
                      : 'text-ink-600 hover:text-ink-900'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              {/* Cart */}
              <button
                onClick={openCart}
                data-testid="cart-button"
                className="relative flex items-center gap-2 group"
              >
                <ShoppingBag className="w-4 h-4 text-ink-900" />
                <span className="text-[13px] tracking-[0.12em] uppercase font-medium text-ink-900">
                  Bag ({cartCount})
                </span>
              </button>
            </div>

            {/* Mobile Row */}
            <div className="lg:hidden flex items-center justify-between w-full">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 -ml-2 text-ink-900"
              >
                {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
              <div className="flex items-center gap-4">
                <button
                  onClick={openCart}
                  data-testid="cart-button"
                  className="relative p-2 text-ink-900"
                >
                  <ShoppingBag className="w-5 h-5" />
                  {cartCount > 0 && (
                    <span className="absolute top-0 right-0 w-4 h-4 bg-copper-500 text-white text-[9px] font-medium flex items-center justify-center rounded-full">
                      {cartCount}
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Menu - Full screen drawer */}
        <div className={`lg:hidden fixed inset-0 top-[116px] bg-cream-50 transition-all duration-500 ${
          isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}>
          <div className="h-full overflow-y-auto px-6 py-8">
            <div className="space-y-1 mb-8">
              <Link to="/shop" className="block py-4 font-display text-3xl text-ink-900 border-b border-ink-100">
                All Eyewear
              </Link>
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/shop?category=${cat.slug}`}
                  className="block py-2.5 text-sm text-ink-600 pl-4"
                >
                  {cat.name}
                </Link>
              ))}
            </div>
            <div className="space-y-1">
              {[...navLeft.filter(i => !i.hasDropdown), ...navRight].map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className="block py-4 font-display text-2xl text-ink-900 border-b border-ink-100"
                >
                  {item.name}
                </Link>
              ))}
              <Link to="/returns" className="block py-4 font-display text-2xl text-ink-900 border-b border-ink-100">
                Returns
              </Link>
            </div>
            <div className="pt-8 space-y-3">
              <a href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`} className="flex items-center gap-3 text-ink-600">
                <Phone className="w-4 h-4" />
                {CONTACT_INFO.phone}
              </a>
              <a href={`mailto:${CONTACT_INFO.email}`} className="flex items-center gap-3 text-ink-600">
                <Mail className="w-4 h-4" />
                {CONTACT_INFO.email}
              </a>
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
